/**
 * BulkChangeStatusDialog
 *
 * Allows changing the project status for one or more selected projects at once.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, ListChecks } from 'lucide-react';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { PROJECT_STATUS_LABELS } from '@/types';

interface BulkChangeStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  /** Called with the chosen statusId when the user confirms */
  onConfirm: (statusId: number) => Promise<void>;
}

export function BulkChangeStatusDialog({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
}: BulkChangeStatusDialogProps) {
  const { selectableStatuses, isLoading } = useProjectStatuses();

  const displayStatuses = selectableStatuses.length > 0
    ? selectableStatuses
    : Object.entries(PROJECT_STATUS_LABELS).map(([id, name]) => ({ id: Number(id), name }));
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const handleConfirm = async () => {
    if (!selectedStatus) return;
    setIsSaving(true);
    try {
      await onConfirm(Number(selectedStatus));
      onOpenChange(false);
      setSelectedStatus('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!isSaving) {
      onOpenChange(open);
      if (!open) setSelectedStatus('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <ListChecks className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle>Change Status</DialogTitle>
          </div>
          <DialogDescription>
            Update the status for{' '}
            <span className="font-semibold text-foreground">
              {selectedCount} {selectedCount === 1 ? 'project' : 'projects'}
            </span>
            . This will overwrite the existing status on all selected projects.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-status-select">New Status</Label>
            <Select
              value={selectedStatus}
              onValueChange={setSelectedStatus}
            >
              <SelectTrigger id="bulk-status-select">
                <SelectValue placeholder="Select a status…" />
              </SelectTrigger>
              <SelectContent>
                  {displayStatuses.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedStatus || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating…
              </>
            ) : (
              `Update ${selectedCount} ${selectedCount === 1 ? 'Project' : 'Projects'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
